import axios from 'axios';
import { logout, setCredentials } from '../../features/auth/store/authSlice';
import { store } from '../../app/store';
 
// The VITE_API_URL from .env already includes /api/v1/
export const API_CONFIG = {
  // Always use the direct URL to the backend
  BASE_URL: import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1',
  ENDPOINTS: {
    LOGIN: 'auth/login',
    LOGIN_AS_CUSTOMER: 'auth/login/customer',
    GOOGLE_LOGIN: 'auth/google',
    MICROSOFT_LOGIN: 'auth/microsoft',
    LINKEDIN_LOGIN: 'auth/linkedin',
    REGISTER: 'auth/register',
    REFRESH_TOKEN: 'auth/token/refresh',
    ACTIVATE: 'auth/activate',
    OTP_REQUEST: 'auth/otp/request',
    OTP_VERIFY: 'auth/otp/verify',
    PASSWORD_RESET: 'auth/password/reset',
    USER_PROFILE: 'auth/current',
    UPDATE_PROFILE: 'auth/me',
  },
};
 
// Configure axios defaults
const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});
 
// Request interceptor for auth token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    const cleanedToken = token.replace(/^"(.*)"$/, '$1');
    config.headers.Authorization = `Bearer ${cleanedToken}`;
  }
  return config;
});
 
// Request interceptor for CSRF token
apiClient.interceptors.request.use((config) => {
  const csrfToken = document.cookie
    .split('; ')
    .find((row) => row.startsWith('XSRF-TOKEN='))
    ?.split('=')[1];
  if (csrfToken) {
    config.headers['X-XSRF-TOKEN'] = csrfToken;
  }
  return config;
});
 
// Response interceptor for token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
 
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
 
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) throw new Error('No refresh token available');
 
        const refreshResponse = await apiClient.post(
          API_CONFIG.ENDPOINTS.REFRESH_TOKEN,
          {},
          {
            headers: {
              'Refresh-Token': refreshToken,
            },
          }
        );
 
        if (refreshResponse.status === 200) {
          const {
            access_token,
            refresh_token,
            token_type,
            expires_in,
            email,
            first_name,
            last_name,
            role_id,
            role_name,
            permissions
          } = refreshResponse.data;

          // The refresh endpoint only ever returns token fields (see
          // generate_new_tokens in backend/app/core/security.py) - it never
          // includes id/email/first_name/last_name/role_id/role_name/
          // permissions. setCredentials fully replaces state.auth.user, so
          // falling back to the currently-cached user here (instead of the
          // undefined values destructured above) keeps a silent background
          // refresh from wiping out the signed-in user's profile info.
          const currentUser = store.getState().auth.user;

          store.dispatch(setCredentials({
            access_token,
            refresh_token,
            token_type,
            expires_in,
            id: currentUser?.id ?? 0,
            email: email ?? currentUser?.email ?? '',
            first_name: first_name ?? currentUser?.first_name ?? '',
            last_name: last_name ?? currentUser?.last_name,
            role_id: role_id ?? currentUser?.role_id ?? 0,
            role_name: role_name ?? currentUser?.role_name,
            permissions: permissions ?? currentUser?.permissions ?? [],
          }));
 
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        console.error('Refresh token failed:', refreshError);
        store.dispatch(logout());
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    }
 
    if (error.response?.status === 401) {
      console.error('Session expired. Please log in again.');
      store.dispatch(logout());
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
 
    return Promise.reject(error);
  }
);
 
// Debug interceptors in development
if (import.meta.env.DEV) {
  apiClient.interceptors.request.use((config) => {
    return config;
  });
 
  apiClient.interceptors.response.use((response) => {
    return response;
  });
}
 
export default apiClient;